import React, { useEffect, useRef, useState } from 'react';

const IDLE_MIN = Number(
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_IDLE_LOGOUT_MIN) || 15
);

const WARN_SEC = Number(
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_IDLE_WARNING_SEC) || 180
);

export default function IdleLogout() {
  const idleMs = IDLE_MIN * 60 * 1000;
  const warnMs = WARN_SEC * 1000;

  const lastActivityRef = useRef(Date.now());
  const [showWarn, setShowWarn] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(warnMs / 1000));

  // Cross-tab channel
  const channelName = 'sk_auth';
  const bcRef = useRef(null);
  if (!bcRef.current && typeof window !== 'undefined' && 'BroadcastChannel' in window) {
    bcRef.current = new BroadcastChannel(channelName);
    bcRef.current.onmessage = (e) => {
      const t = e?.data?.type;
      if (t === 'activity') markActivity(false);
      if (t === 'logout') doLogout(false);
    };
  }
  function broadcast(msg) { try { bcRef.current?.postMessage(msg); } catch {} }

  function doLogout(broadcastIt = true) {
    try { 
      fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    } catch {}
    try {
      // clear common auth keys if used
      localStorage.removeItem('sk_auth');
    } catch {}
    if (broadcastIt) broadcast({ type: 'logout' });
    window.location.assign('/');
  }

  function markActivity(broadcastIt = true) {
    lastActivityRef.current = Date.now();
    setShowWarn(false);
    if (broadcastIt) broadcast({ type: 'activity' });
    armTimers();
  }

  const idleTimerRef = useRef(null);
  const warnTickRef = useRef(null);
  const warnTimeoutRef = useRef(null);

  function clearTimers() {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (warnTickRef.current) { clearInterval(warnTickRef.current); warnTickRef.current = null; }
    if (warnTimeoutRef.current) { clearTimeout(warnTimeoutRef.current); warnTimeoutRef.current = null; }
  }

  function startWarning() {
    setShowWarn(true);
    setSecondsLeft(Math.ceil(warnMs / 1000));
    const start = Date.now();
    warnTickRef.current = setInterval(() => {
      const left = Math.max(0, warnMs - (Date.now() - start));
      setSecondsLeft(Math.ceil(left / 1000));
    }, 1000);
    warnTimeoutRef.current = setTimeout(() => {
      clearTimers();
      doLogout(true);
    }, warnMs);
  }

  function armTimers() {
    clearTimers();
    const elapsed = Date.now() - lastActivityRef.current;
    const remainToIdle = Math.max(0, idleMs - elapsed);
    idleTimerRef.current = setTimeout(startWarning, remainToIdle);
  }

  useEffect(() => {
    const activity = () => markActivity(true);
    const events = ['mousemove','mousedown','keydown','wheel','touchstart','scroll'];
    events.forEach(ev => window.addEventListener(ev, activity, { passive: true }));
    document.addEventListener('visibilitychange', () => { if (!document.hidden) activity(); });
    window.addEventListener('focus', activity);
    window.addEventListener('online', activity);

    armTimers();

    return () => {
      events.forEach(ev => window.removeEventListener(ev, activity));
      window.removeEventListener('focus', activity);
      window.removeEventListener('online', activity);
      clearTimers();
      try { bcRef.current?.close(); } catch {}
    };
  }, []);

  if (!showWarn) return null;

  return (
    <div style={{
      position:'fixed', inset:0, background:'rgba(0,0,0,0.4)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex: 50
    }}>
      <div style={{
        background:'var(--card-bg, #fff)', color:'var(--card-fg, #111)',
        borderRadius:12, boxShadow:'0 10px 30px rgba(0,0,0,0.3)', padding:20, width:'min(480px, 92vw)'
      }}>
        <div style={{ fontWeight:600, fontSize:18, marginBottom:6 }}>You’ve been idle</div>
        <div style={{ marginBottom:14 }}>
          You’ll be logged out in <b>{secondsLeft}s</b> due to inactivity.
        </div>
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
          <button onClick={() => doLogout(true)} style={{ padding:'8px 12px' }}>
            Logout now
          </button>
          <button onClick={() => markActivity(true)} style={{ padding:'8px 12px', fontWeight:600 }}>
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}
